
import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { HiSparkles, HiCamera } from 'react-icons/hi'
import './MenuLoadingModal.css'

function MenuLoadingModal({ show }) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0)
  const [videoPath, setVideoPath] = useState('')
  const videoRef = useRef(null)

  const messages = [
    "Scanning menu...",
    "Detecting dishes...",
    "Analyzing ingredients...",
    "Creating your poll..."
  ]

  // API base URL with fallback
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || ''

  // Fetch active video from server API
  useEffect(() => {
    const fetchActiveVideo = async () => {
      try {
        const response = await fetch(`${API_BASE_URL} /api/admin / active - video`)
        if (response.ok) {
          const data = await response.json()
          setVideoPath(data.videoUrl || '')
        }
      } catch (error) {
        console.warn('Failed to fetch active video:', error)
        setVideoPath('')
      }
    }
    if (show) {
      fetchActiveVideo()
    }
  }, [show, API_BASE_URL])

  useEffect(() => {
    if (!show) return

    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % messages.length)
    }, 2000) // Change message every 2 seconds

    return () => clearInterval(interval)
  }, [show, messages.length])

  // Handle video playback - trigger on videoPath changes
  useEffect(() => {
    if (!show || !videoRef.current || !videoPath) return

    const video = videoRef.current
    // Reload video when path changes
    video.load()
    video.play().catch((error) => {
      console.warn('Video autoplay failed:', error)
    })

    return () => {
      if (video) {
        video.pause()
        video.currentTime = 0
      }
    }
  }, [show, videoPath])

  if (!show) return null

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        animation: 'fade-in 0.3s ease-out',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)'
      }}
    >
      <div
        className="menu-loading-modal-content"
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderRadius: 'var(--radius-xl)',
          border: '1px solid var(--glass-border)',
          boxShadow: 'var(--shadow-xl)',
          padding: '3rem 2rem',
          maxWidth: '600px',
          width: '90%',
          maxHeight: '90vh',
          overflow: 'hidden',
          animation: 'slide-in 0.4s ease-out',
          textAlign: 'center',
          position: 'relative'
        }}
      >
        {/* Video background/overlay - only render if videoPath exists */}
        {videoPath ? (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              overflow: 'hidden',
              borderRadius: 'var(--radius-xl)',
              opacity: 1,
              zIndex: 0
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              loop
              muted
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'brightness(0.8) saturate(1.1)'
              }}
            >
              <source src={videoPath} type="video/mp4" />
              <source src={videoPath.replace('.mp4', '.webm')} type="video/webm" />
            </video>
          </div>
        ) : (
          /* Professional animated fallback when no video is configured */
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: 'var(--radius-xl)',
              background: 'radial-gradient(ellipse at center, rgba(59, 130, 246, 0.1) 0%, rgba(15, 15, 15, 0.98) 70%)',
              overflow: 'hidden',
              zIndex: 0
            }}
          >
            {/* Animated floating orbs */}
            <div style={{
              position: 'absolute',
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(245, 158, 11, 0.3) 0%, transparent 70%)',
              top: '10%',
              left: '20%',
              animation: 'drift 12s ease-in-out infinite',
              filter: 'blur(20px)'
            }} />
            <div style={{
              position: 'absolute',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(59, 130, 246, 0.3) 0%, transparent 70%)',
              top: '60%',
              right: '15%',
              animation: 'drift-reverse 10s ease-in-out infinite',
              filter: 'blur(15px)'
            }} />
            <div style={{
              position: 'absolute',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(239, 68, 68, 0.25) 0%, transparent 70%)',
              bottom: '20%',
              left: '10%',
              animation: 'drift 14s ease-in-out infinite',
              animationDelay: '-3s',
              filter: 'blur(18px)'
            }} />
            <div style={{
              position: 'absolute',
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(249, 115, 22, 0.35) 0%, transparent 70%)',
              top: '30%',
              right: '25%',
              animation: 'drift-reverse 8s ease-in-out infinite',
              animationDelay: '-2s',
              filter: 'blur(12px)'
            }} />
            <div style={{
              position: 'absolute',
              width: '90px',
              height: '90px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(16, 185, 129, 0.2) 0%, transparent 70%)',
              bottom: '10%',
              right: '30%',
              animation: 'drift 16s ease-in-out infinite',
              animationDelay: '-5s',
              filter: 'blur(16px)'
            }} />
          </div>
        )}

        {/* Floating dish icons - positioned relative to modal */}
        <div
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            animation: 'float 3s ease-in-out infinite',
            animationDelay: '0s',
            opacity: 0.6,
            zIndex: 2
          }}
        >
          <div style={{
            width: '24px',
            height: '18px',
            backgroundColor: '#f59e0b',
            borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
            border: '2px solid #d97706'
          }} />
        </div>

        <div
          style={{
            position: 'absolute',
            top: '30px',
            right: '30px',
            animation: 'float 3s ease-in-out infinite',
            animationDelay: '1s',
            opacity: 0.6,
            zIndex: 2
          }}
        >
          <div style={{
            width: '20px',
            height: '15px',
            backgroundColor: '#f97316',
            borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
            border: '2px solid #ea580c'
          }} />
        </div>

        <div
          style={{
            position: 'absolute',
            bottom: '30px',
            left: '30px',
            animation: 'float 3s ease-in-out infinite',
            animationDelay: '2s',
            opacity: 0.6,
            zIndex: 2
          }}
        >
          <div style={{
            width: '18px',
            height: '14px',
            backgroundColor: '#ef4444',
            borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
            border: '2px solid #dc2626'
          }} />
        </div>

        {/* Rotating utensils - fork */}
        <div
          style={{
            position: 'absolute',
            top: '50px',
            right: '50px',
            animation: 'rotate 4s linear infinite',
            animationDelay: '0.5s',
            transformOrigin: 'center center',
            zIndex: 2
          }}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            alignItems: 'center'
          }}>
            <div style={{
              width: '16px',
              height: '2px',
              backgroundColor: '#f59e0b',
              borderRadius: '1px'
            }} />
            <div style={{
              width: '12px',
              height: '2px',
              backgroundColor: '#f59e0b',
              borderRadius: '1px'
            }} />
            <div style={{
              width: '16px',
              height: '2px',
              backgroundColor: '#f59e0b',
              borderRadius: '1px'
            }} />
          </div>
        </div>

        {/* Rotating utensils - spoon */}
        <div
          style={{
            position: 'absolute',
            bottom: '50px',
            right: '20px',
            animation: 'rotate 4s linear infinite reverse',
            animationDelay: '1.5s',
            transformOrigin: 'center center',
            zIndex: 2
          }}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              backgroundColor: '#ef4444',
              borderRadius: '50% 50% 50% 0',
              marginBottom: '2px'
            }} />
            <div style={{
              width: '12px',
              height: '2px',
              backgroundColor: '#ef4444',
              borderRadius: '1px'
            }} />
          </div>
        </div>

        {/* Content overlay */}
        <div style={{
          position: 'relative',
          zIndex: 1,
          padding: '2rem'
        }}>

          {/* Main pulsing icon */}
          <div
            style={{
              marginBottom: '2rem',
              animation: 'pulse-scale 2s ease-in-out infinite'
            }}
          >
            <HiCamera style={{
              fontSize: '4rem',
              color: '#f59e0b',
              filter: 'drop-shadow(0 0 20px rgba(245, 158, 11, 0.5))'
            }} />
          </div>

          {/* Title */}
          <h2 style={{
            margin: '0 0 1rem 0',
            fontSize: '1.75rem',
            fontWeight: '700',
            color: 'var(--text-primary)',
            background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 50%, #ef4444 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textShadow: '0 0 20px rgba(0, 0, 0, 0.9), 0 2px 10px rgba(0, 0, 0, 0.8)',
            filter: 'drop-shadow(0 0 8px rgba(0, 0, 0, 0.9))'
          }}>
            Creating Your Poll...
          </h2>

          {/* Cycling message */}
          <p
            key={currentMessageIndex}
            style={{
              margin: '0 0 2rem 0',
              color: '#ffffff',
              fontSize: '1.1rem',
              fontWeight: '600',
              animation: 'slide-up-fade 0.5s ease-out',
              textShadow: '0 0 15px rgba(0, 0, 0, 0.95), 0 2px 8px rgba(0, 0, 0, 0.9), 0 0 4px rgba(0, 0, 0, 1)'
            }}
          >
            {messages[currentMessageIndex]}
          </p>

          {/* Progress indicator */}
          <div
            style={{
              width: '100%',
              height: '4px',
              backgroundColor: 'var(--bg-tertiary)',
              borderRadius: '2px',
              overflow: 'hidden',
              marginBottom: '1rem',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div
              style={{
                height: '100%',
                background: 'linear-gradient(90deg, #f59e0b 0%, #f97316 50%, #ef4444 100%)',
                borderRadius: '2px',
                animation: 'progress-glow 2s ease-in-out infinite',
                width: '100%'
              }}
            />
          </div>
        </div>

        {/* Sparkle effects - positioned outside content overlay */}
        <div
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            animation: 'float 2.5s ease-in-out infinite',
            animationDelay: '0.3s',
            zIndex: 2
          }}
        >
          <HiSparkles style={{
            fontSize: '1.5rem',
            color: '#f59e0b',
            opacity: 0.8,
            filter: 'drop-shadow(0 0 10px rgba(245, 158, 11, 0.5))'
          }} />
        </div>

        <div
          style={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            animation: 'float 2.5s ease-in-out infinite',
            animationDelay: '1.8s',
            zIndex: 2
          }}
        >
          <HiSparkles style={{
            fontSize: '1.2rem',
            color: '#f97316',
            opacity: 0.8,
            filter: 'drop-shadow(0 0 10px rgba(249, 115, 22, 0.5))'
          }} />
        </div>
      </div>
    </div>,
    document.body
  )
}

export default MenuLoadingModal
